import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { useBodyClass } from '@src/hooks/core/core-hook-imports';
import { RadioOptionList, SearchBox } from '@src/components/core/core-component-imports';
import { MidnightTimesIcon } from '@src/images/image-imports';
import { FILTER_IDS } from '@src/js/constants';
import { ObjectUtils } from '@js/utils/utils-imports.es6';
import { CLASS_NAMES } from '@js/constants/constants-imports';

/**
 * Renders a list of radio options, which is what displays when the filter is clicked.
 */
const SearchFilterForm = ({
  name,
  queryParameterName,
  options: initialOptions,
  defaultOptionId,
  selectedOptionId: initialSelectedOptionId,
  selectionHandler,
  applyFilterLabel,
  clearLabel,
  allowSearch,
  searchPlaceholder,
  numberOfOptionsBeforeSearch,
  closeHandler,
  closing,
}) => {
  const [, setSearchParams] = useSearchParams();

  const [options, setOptions] = useState(initialOptions);
  const [selectedOptionId, setSelectedOptionId] = useState(initialSelectedOptionId);
  const [searchInput, setSearchInput] = useState('');

  const numberOfInitialOptions = Object.keys(initialOptions).length;
  const showSearch = allowSearch && (numberOfInitialOptions > numberOfOptionsBeforeSearch);

  const onRadioChange = (id) => {
    if (selectedOptionId === id) {
      setSelectedOptionId(null);
    }
    else {
      setSelectedOptionId(id);
    }
  };

  const onClear = () => {
    applySelection(defaultOptionId ?? null);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    applySelection(selectedOptionId);
  };

  const applySelection = (id) => {
    closeHandler();

    setSearchParams((prev) => {
      if (id) {
        prev.set(queryParameterName, id);
      }
      else {
        prev.delete(queryParameterName);
      }
      prev.set(FILTER_IDS.PAGE, 1);
      return prev;
    });

    if (selectionHandler) {
      selectionHandler(selectedOptionId);
    }
  };

  const onSearchOptions = (input) => {
    setSearchInput(input);
    const keepOption = ([, { label }]) => label.toLowerCase().includes(input.toLowerCase());
    const filteredOptions = ObjectUtils.filter(initialOptions, keepOption);
    setOptions(filteredOptions);
  };

  useBodyClass(CLASS_NAMES.DISABLE_SCROLL_MOBILE);

  return (
    <>
      <div className='mobile-overlay mobile-only active' onClick={closeHandler}></div>
      <form className={`filter-form ${closing ? '' : 'active'}`} onSubmit={onSubmit} data-testid='search-filter-form'>
        <button className='close-button styleless-button mobile-only' type='button' onClick={closeHandler}>
          <MidnightTimesIcon />
        </button>
        <p className='filter-by-text'>{name}</p>
        {
          showSearch &&
          <SearchBox placeholder={searchPlaceholder} changeHandler={onSearchOptions} />
        }
        <div className='filters-radio-input--container'>
          <RadioOptionList
            options={options}
            selectedOptionId={selectedOptionId}
            textToBold={searchInput}
            selectionHandler={onRadioChange}
          />
        </div>
        <div className='clear-apply--wrapper'>
          <label htmlFor='clear-button'>
            {
              initialSelectedOptionId &&
              <input id='clear-button' type='reset' className='clear-button styleless-button' readOnly='' value={clearLabel} onClick={onClear} />
            }
          </label>
          <label htmlFor='apply-button'>
            <input id='apply-button' type='submit' className='apply-button styleless-button' readOnly='' value={applyFilterLabel} />
          </label>
        </div>
      </form>
    </>
  );
}

SearchFilterForm.propTypes = {
  /**
   * the name to display on the filter, by default
   */
  name: PropTypes.string.isRequired,
  /**
   * the query parameter whose value should change with the filter's value
   */
  queryParameterName: PropTypes.string.isRequired,
  /**
   * the options to display to the user for selection
   *
   * @example
   * {
   *  'primary-care': {
   *    label: 'Primary Care',
   *    disabled: false,
   *  },
   *  'oncology': {
   *    label: 'Oncology',
   *    disabled: true,
   *  },
   *  'behavioral-health': {
   *    label: 'Behavioral Health',
   *    disabled: false,
   *  },
   * }
   */
  options: PropTypes.object.isRequired,
  /**
   * the option to default to when no selection is made, if any is desired
   */
  defaultOptionId: PropTypes.string,
  /**
   * the ID of the currently selected filter option
   */
  selectedOptionId: PropTypes.string,
  /**
   * the callback function for when the a selection is applied
   */
  selectionHandler: PropTypes.func,
  /**
   * the text to display for the button to apply a filter change
   */
  applyFilterLabel: PropTypes.string.isRequired,
  /**
   * the text to display to clear all filter options
   */
  clearLabel: PropTypes.string.isRequired,
  /**
   * whether or not to show a search box for filtering options if there are more
   * options than the specified numberOfOptionsBeforeSearch
   */
  allowSearch: PropTypes.bool,
  /**
   * the text to display in the search box, by default, when there are enough
   * options to show a search box to filter them (required if allowSearch is true)
   */
  searchPlaceholder: PropTypes.string,
  /**
   * the number of options to display before a search box is displayed that
   * allows the user to filter the available options
   */
  numberOfOptionsBeforeSearch: PropTypes.number.isRequired,
  /**
   * the callback function for when the close button is clicked
   */
  closeHandler: PropTypes.func.isRequired,
  /**
   * if true, indicates that the search filter form is in the process of being
   * closed (for animation purposes)
   */
  closing: PropTypes.bool,
};

SearchFilterForm.defaultProps = {
  allowSearch: true,
  closing: false,
};

export default SearchFilterForm;
